import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom"; // Adicionado useLocation
import MainLayout from "../../../../layouts/MainLayout";
import "../../../../assets/styles/custom-styles.css";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useForm from "../../../../hooks/useForm";
import { contactFields } from "../../../../constants/forms/contactFields"; // Assumindo que este arquivo existe e está correto
import Form from "../../../../components/forms/Form";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import { useMutation, useQueryClient } from "@tanstack/react-query"; // Importado TanStack Query
import { cleanPayload } from "../../../../utils/objectUtils";

const CreateCustomerContactPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  // useBaseService agora expõe setFormErrors para lidar com erros de validação da API
  const {
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, handleChange, resetForm } = useForm(
    setDefaultFieldValues(contactFields),
  );

  // --- Mutação para criar um novo Contato ---
  const createContactMutation = useMutation({
    mutationFn: async (dataToCreate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePost(
        entities.customers.contacts.create(id),
        dataToCreate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        resetForm();
        queryClient.invalidateQueries({ queryKey: ["customerContacts", id] });
        navigate(`/aluguel/clientes/detalhes/${id}`, {
          state: { type: "success" },
        });
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const payload = {
        contact: {
          ...cleanedData.contact,
          phone: cleanedData?.contact.phone
            ? String(cleanedData?.contact.phone)
            : null,
          cell: cleanedData?.contact.cell
            ? String(cleanedData?.contact.cell)
            : null,
        },
      };
      await createContactMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao cadastrar contato:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, createContactMutation]);

  const handleBack = useCallback(() => {
    navigate(`/aluguel/clientes/detalhes/${id}`);
  }, [id, navigate]);

  // --- Efeito para exibir notificações de estado da rota ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  // --- Renderização do formulário ---
  const renderForm = useCallback(() => {
    const renderedSections = new Set();

    return contactFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={() => {}}
            getSelectedValue={() => {}}
          />
        </div>
      );
    });
  }, [formData, formErrors, handleChange]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Contato do Cliente"
        showBackButton={true}
        backUrl={`/aluguel/clientes/detalhes/${id}`}
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando"
          handleBack={handleBack}
          isLoading={createContactMutation.isPending}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateCustomerContactPage;
